import React, { useMemo } from "react";
import { useGlobalM2Chart } from "@/hooks/coinglass/useGlobalM2Chart";
import EChartsRenderer from "@/components/charts/base/EChartsRenderer";
import type { EChartsOption } from "echarts";

interface GlobalM2ChartProps {
  startDate?: string;
  endDate?: string;
  limit?: number;
  height?: string | number;
}

export const GlobalM2Chart: React.FC<GlobalM2ChartProps> = ({
  startDate,
  endDate,
  limit,
  height = 600,
}) => {
  const { data, isLoading, isError, error } = useGlobalM2Chart({
    start_date: startDate || undefined,
    end_date: endDate || undefined,
    limit: limit || undefined,
  });

  const option = useMemo<EChartsOption | null>(() => {
    if (!data) return null;

    const { xAxis, series } = data;

    return {
      backgroundColor: "transparent",
      tooltip: {
        trigger: "axis",
        backgroundColor: "rgba(20, 20, 30, 0.9)",
        borderColor: "#333",
        textStyle: {
          color: "#fff",
        },
        axisPointer: {
          type: "cross",
          label: {
            backgroundColor: "#6a7985",
          },
        },
        formatter: (params: echarts.TooltipComponentFormatterCallbackParams) => {
          if (!Array.isArray(params) || params.length === 0) return "";

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const p = params as any[];
          const date = p[0].axisValue;

          // p[0] is M2 YoY Growth (bar)
          // p[1] is M2 Supply (line)
          // p[2] is BTC Price (line)
          const m2Growth = (p[0]?.data as number) || 0;
          const m2Supply = (p[1]?.data as number) || 0;
          const btcPrice = p[2]?.data as number | undefined;

          const growthColor = m2Growth >= 0 ? "#10b981" : "#ef4444";

          return `
            <div style="padding: 8px; min-width: 280px;">
              <div style="font-weight: bold; margin-bottom: 8px; color: #9ca3af;">${date}</div>
              
              <div style="margin-top: 10px; border-top: 1px solid #374151; padding-top: 8px;">
                <div style="display: flex; align-items: center; margin-bottom: 6px;">
                  <span style="display: inline-block; width: 8px; height: 8px; border-radius: 2px; background-color: ${growthColor}; margin-right: 6px;"></span>
                  <span style="color: #d1d5db;">M2 YoY Growth:</span>
                  <span style="margin-left: auto; font-weight: bold; color: ${growthColor}; padding-left: 12px;">${m2Growth >= 0 ? "+" : ""}${m2Growth.toFixed(2)}%</span>
                </div>

                <div style="display: flex; align-items: center; margin-bottom: 6px;">
                  <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background-color: #3b82f6; margin-right: 6px;"></span>
                  <span style="color: #d1d5db;">M2 Supply:</span>
                  <span style="margin-left: auto; font-weight: bold; color: #3b82f6; padding-left: 12px;">$${(m2Supply / 1e12).toFixed(2)}T</span>
                </div>

                ${
                  btcPrice !== undefined
                    ? `
                <div style="margin-top: 8px; padding-top: 6px; border-top: 1px solid #374151;">
                  <div style="display: flex; align-items: center;">
                    <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background-color: #fbbf24; margin-right: 6px;"></span>
                    <span style="color: #d1d5db;">BTC Price:</span>
                    <span style="margin-left: auto; font-weight: bold; color: #fbbf24; padding-left: 12px;">$${btcPrice.toLocaleString(
                      "en-US",
                      { minimumFractionDigits: 0, maximumFractionDigits: 0 }
                    )}</span>
                  </div>
                </div>
                `
                    : ""
                }
              </div>
            </div>
          `;
        },
      },
      legend: {
        data: ["Global M2 YoY Growth", "Global M2 Supply", "BTC Price"],
        top: 0,
        right: 0,
        textStyle: {
          color: "#9ca3af",
        },
        icon: "circle",
      },
      grid: {
        left: "3%",
        right: "12%",
        bottom: "15%",
        top: "12%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        boundaryGap: true, // true for bar charts
        data: xAxis,
        axisLine: {
          lineStyle: {
            color: "#374151",
          },
        },
        axisLabel: {
          color: "#9ca3af",
          hideOverlap: true,
          formatter: (value: string) => {
            const date = new Date(value);
            return date.toLocaleDateString("en-US", {
              month: "short",
              year: "numeric",
            });
          },
        },
      },
      yAxis: [
        {
          type: "value",
          name: "M2 YoY Growth (%)",
          position: "left",
          axisLine: {
            show: true,
            lineStyle: {
              color: "#10b981",
            },
          },
          splitLine: {
            lineStyle: {
              color: "#1f2937",
            },
          },
          axisLabel: {
            color: "#9ca3af",
            formatter: (value: number) => `${value.toFixed(0)}%`,
          },
        },
        {
          type: "value",
          name: "M2 Supply",
          position: "right",
          offset: 0,
          axisLine: {
            show: true,
            lineStyle: {
              color: "#3b82f6",
            },
          },
          splitLine: {
            show: false,
          },
          axisLabel: {
            color: "#3b82f6",
            formatter: (value: number) => `$${(value / 1e12).toFixed(0)}T`,
          },
        },
        {
          type: "value",
          name: "BTC Price (USD)",
          position: "right",
          offset: 80,
          axisLine: {
            show: true,
            lineStyle: {
              color: "#fbbf24",
            },
          },
          splitLine: {
            show: false,
          },
          axisLabel: {
            color: "#fbbf24",
            formatter: (value: number) => {
              if (value >= 1000) {
                return `$${(value / 1000).toFixed(0)}k`;
              }
              return `$${value}`;
            },
          },
        },
      ],
      series: series.map((s, idx) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const baseSeries: any = {
          ...s,
        };

        // idx 0: M2 YoY Growth (bar chart with conditional coloring)
        // idx 1: M2 Supply (line chart, blue)
        // idx 2: BTC Price (line chart, gold)
        if (idx === 0) {
          // Bar chart for M2 Growth - color each bar based on positive/negative
          baseSeries.itemStyle = {
            color: (params: { data: number }) => {
              return params.data >= 0 ? "#10b981" : "#ef4444";
            },
          };
          baseSeries.barWidth = "60%";
        } else if (idx === 1) {
          // M2 Supply line
          baseSeries.itemStyle = {
            color: "#3b82f6",
          };
          baseSeries.lineStyle = {
            width: 2,
            color: "#3b82f6",
          };
          baseSeries.symbol = "none";
          baseSeries.smooth = true;
        } else {
          // BTC Price line
          baseSeries.itemStyle = {
            color: "#fbbf24",
          };
          baseSeries.lineStyle = {
            width: 2,
            color: "#fbbf24",
          };
          baseSeries.symbol = "none";
          baseSeries.smooth = true;
        }

        return baseSeries;
      }),
      dataZoom: [
        {
          type: "inside",
          start: 0,
          end: 100,
        },
        {
          type: "slider",
          show: true,
          bottom: 30,
          height: 20,
          borderColor: "#374151",
          textStyle: { color: "#9ca3af" },
          brushSelect: false,
        },
      ],
    };
  }, [data]);

  return (
    <EChartsRenderer
      chartOption={option || {}}
      height={height}
      isLoading={isLoading}
      error={isError ? (error as Error).message : null}
      hasData={!!data}
      containerClassName="w-full h-full"
    />
  );
};
